import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte,
    AangifteRapport, BokkzAlgDataService, IncomeTaxEntity,
    Onderneming, Ondernemingsvorm,
    SpecificatieJaarrekening

} from '@snelstart-ventures/fiscaal/domain-logic';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SpecBeginvermogenComponent } from './spec-beginvermogen/spec-beginvermogen.component';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule, DecimalPipe } from '@angular/common';
import { SpecEindvermogenComponent } from './spec-eindvermogen/spec-eindvermogen.component';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-vermogensvergelijking',
    templateUrl: './vermogensvergelijking.component.html',
    styleUrls: ['./vermogensvergelijking.component.scss'],
    imports: [
        SharedUiDetailsModule,
        SpecBeginvermogenComponent,
        SharedUiFormControlsModule,
        SharedUiAlertModule,
        DecimalPipe,
        SpecEindvermogenComponent,
        CommonModule
    ]
})
export class VermogensvergelijkingComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();
    private rapportSubscription: Subscription | undefined;
    private isSaving = false;
    public incomeTaxEntity = {} as IncomeTaxEntity;

    public partnerBoolean: boolean = false;
    public clientId!: string;
    @Input() partner = new BehaviorSubject<boolean>(false);
    private aangeverRapport = {} as AangifteRapport;
    private partnerRapport = {} as AangifteRapport;

    private focusedElementId: string | null = null;

    public specJaarrekeningFg = {} as FormGroup;
    specJaarr = {} as AangifteRapport[];
    public ondernemingFG = {} as FormGroup;
    public tellingFG = {} as FormGroup;
    public formStructure: any;

    public heeftSamenwerkingsverband: boolean = false;

    public labelBalansOpening = '';
    public labelBalansSlot = '';

    public showSpinner = true;

    constructor(public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService, private router: Router, private fb: FormBuilder) {
        this.ondernemingFG = this.fb.group({
            ondernemingen: this.fb.array([])
        });
        this.specJaarrekeningFg = this.fb.group({
            specificatieJaarrekening: this.fb.array<SpecificatieJaarrekening>([
                    this.fb.group({
                        opbouw: this.fb.group({
                            beginVermogen: null as unknown as number,
                            eindVermogen: null as unknown as number,
                            totaleOnttrekkingen: null as unknown as number,
                            totaleStortingen: null as unknown as number,
                            winstVolgensResultatenRekening: null as unknown as number,
                            aandeelInWinstVolgensResultatenRekening: null as unknown as number,
                            totaalActiefOpeningsbalans: null as unknown as number,
                            totaalPassiefOpeningsbalans: null as unknown as number,
                            totaalActiefSlotbalans: null as unknown as number,
                            totaalPassiefSlotbalans: null as unknown as number
                        })
                    })
                ]
            )
        });
        this.tellingFG = this.fb.group({
            winstVolgensVergelijking: null as unknown as number,
            verschilVermogen: null as unknown as number,
            verschilWinst: null as unknown as number,
            verschilOpeningsbalans: null as unknown as number,
            verschilSlotbalans: null as unknown as number
        });
    }

    get specificatieJaarrekening(): FormArray {
        return this.specJaarrekeningFg.get('specificatieJaarrekening') as FormArray;
    }

    get ondernemingen(): FormArray {
        return this.ondernemingFG.get('ondernemingen') as FormArray;
    }

    createOndernemingFormGroup(onderneming?: Onderneming) {
        return this.dataService.createOndernemingFormGroup(onderneming);
    }

    calculateVerschilVermogen(rapport: AangifteRapport): number {
        let verschilVermogen = 0;
        const ev = rapport.specificatieWUO?.opbouw;
        if (ev) {
            const eindVermogen = ev.totaleEindvermogen ?? 0;
            const beginVermogen = ev.totaleBeginvermogen ?? 0;
            verschilVermogen = eindVermogen - beginVermogen;
        }
        this.tellingFG.patchValue({ verschilVermogen });
        return verschilVermogen;
    }

    calculateWinstVolgensVergelijking(rapport: AangifteRapport): number {
        let winstVolgensVergelijking = 0;
        if (rapport.specificatieJaarrekening?.at(0)?.opbouw) {
            const eindVermogen = rapport.specificatieJaarrekening?.at(0)?.opbouw?.eindVermogen ?? 0;
            const beginVermogen = rapport.specificatieJaarrekening?.at(0)?.opbouw?.beginVermogen ?? 0;
            const verschilVermogen = eindVermogen - beginVermogen;

            const onttrekkingen = rapport.specificatieJaarrekening?.at(0)?.opbouw?.totaleOnttrekkingen ?? 0;
            const stortingen = rapport.specificatieJaarrekening?.at(0)?.opbouw?.totaleStortingen ?? 0;

            winstVolgensVergelijking = verschilVermogen + onttrekkingen - stortingen;
        }
        this.tellingFG.patchValue({ winstVolgensVergelijking });
        return winstVolgensVergelijking;
    }

    calculateVerschilWinst(): number {
        let verschilWinst = 0;
        const opbouw = this.specJaarrekeningFg.get('specificatieJaarrekening.0.opbouw');
        const winstVolgensResRek = opbouw?.get('winstVolgensResultatenRekening')?.value ?? 0;
        const aandeelInWinstVolgensResultatenRekening = opbouw?.get('aandeelInWinstVolgensResultatenRekening')?.value ?? 0;
        const winstVolgensVergelijking = this.tellingFG.get('winstVolgensVergelijking');
        if (opbouw && winstVolgensVergelijking) {
            const verschil = winstVolgensVergelijking.value ?? 0;

            if (!this.heeftSamenwerkingsverband) {
                verschilWinst = Math.abs(verschil - winstVolgensResRek);
            } else {
                verschilWinst = Math.abs(verschil - aandeelInWinstVolgensResultatenRekening);
            }
        }

        this.tellingFG.patchValue({ verschilWinst });
        return verschilWinst;
    }

    private resetFormGroups() {
        const specificatieJaarrekeningArray = this.specJaarrekeningFg.get('specificatieJaarrekening') as FormArray;
        specificatieJaarrekeningArray.at(0)?.setValue({
            opbouw: {
                beginVermogen: null as unknown as number,
                eindVermogen: null as unknown as number,
                aandeelInWinstVolgensResultatenRekening: null as unknown as number,
                totaleOnttrekkingen: null as unknown as number,
                totaleStortingen: null as unknown as number,
                winstVolgensResultatenRekening: null as unknown as number,
                totaalActiefOpeningsbalans: null as unknown as number,
                totaalPassiefOpeningsbalans: null as unknown as number,
                totaalActiefSlotbalans: null as unknown as number,
                totaalPassiefSlotbalans: null as unknown as number
            }
        }, { emitEvent: false });
        this.tellingFG.setValue({
            winstVolgensVergelijking: null as unknown as number,
            verschilVermogen: null as unknown as number,
            verschilWinst: null as unknown as number,
            verschilOpeningsbalans: null as unknown as number,
            verschilSlotbalans: null as unknown as number
        }, { emitEvent: false });
    }

    public ngOnInit(): void {
        // separate partner subscription
        this.subscriptions.add(
            this.partner.subscribe((partner) => {
                this.partnerBoolean = partner;
                if (this.incomeTaxEntity.aangifte) {
                    this.updateForm();
                    this.updateRapportData(); // Dit zorgt ervoor dat de juiste rapport subscription wordt gemaakt
                    this.updateLabels();
                    this.updateHeeftSamenwerkingsverband();
                }
            })
        );

        this.subscriptions.add(
            this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
                this.incomeTaxEntity = incomeTaxEntity;
                if (incomeTaxEntity.aangifte) {
                    this.updateForm();
                    this.updateRapportData();
                    this.updateLabels();
                    this.updateHeeftSamenwerkingsverband();
                }
            })
        );

        this.subscriptions.add(
            this.dataAlgService.client.subscribe((client) => {
                this.clientId = client.sstRelationId!;
            })
        );

        this.subscriptions.add(
            this.router.events
                .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    this.handleUrlChange(event.urlAfterRedirects);
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.rapportSubscription) {
            this.rapportSubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.ondernemingFG.dirty || this.specJaarrekeningFg.dirty || this.tellingFG.dirty;
    }

    // methode om heeftSamenwerkingsverband te updaten
    private updateHeeftSamenwerkingsverband(): void {
        const ondernemingen = this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen
            : this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen;

        if (ondernemingen && ondernemingen.length > 0 && ondernemingen[0].ondernemingsvorm) {
            this.heeftSamenwerkingsverband = ondernemingen[0].ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;
        } else {
            this.heeftSamenwerkingsverband = false;
        }
    }

    // methode om labels te updaten
    private updateLabels(): void {
        const onderneming = this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen?.[0]
            : this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen?.[0];

        if (!onderneming) return;

        if (onderneming) {
            let begindatum = new Date;
            if (onderneming.startOnderneming != null) {
                begindatum = onderneming.startOnderneming
            } else {
                begindatum = new Date(this.incomeTaxEntity.aangifte!.aangifteJaar, 0, 1);
            }
            let slotdatum = new Date;
            if (onderneming.datumStaking != null) {
                slotdatum = onderneming.datumStaking
            } else {
                slotdatum = new Date(this.incomeTaxEntity.aangifte!.aangifteJaar, 11, 31);
            }
            this.labelBalansOpening = begindatum != null
                ? `${new Date(begindatum).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                })}`
                : 'balans';
            this.labelBalansSlot = slotdatum != null
                ? `${new Date(slotdatum).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                })}`
                : 'balans';
        }

        this.disableFormGroup(this.specificatieJaarrekening.at(0));
        this.disableFormGroup(this.tellingFG);
    }

    // methode om rapport data te updaten
    private updateRapportData(): void {
        if (this.rapportSubscription) {
            this.rapportSubscription.unsubscribe();
        }

        // ← Direct resetten bij switchen, niet wachten op nieuwe rapport
        this.resetFormGroups();

        const rapportObservable = this.partnerBoolean
            ? this.dataService.partnerAangifteRapport
            : this.dataService.aangifteRapport;

        this.rapportSubscription = rapportObservable.subscribe((rapport) => {
            if (this.partnerBoolean) {
                this.partnerRapport = rapport;
            } else {
                this.aangeverRapport = rapport;
            }
            this.berekenVermogensvergelijking(rapport);
        });
    }

    // gebruik partnerBoolean direct in plaats van subscribe
    private updateForm(): void {
        const ondernemingenLijst = this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || []
            : this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];

        const formArray = this.ondernemingFG.get('ondernemingen') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        while (formArray.length > ondernemingenLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        // Update ondernemingen
        for (let i = 0; i < ondernemingenLijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(ondernemingenLijst[i]);
            } else {
                const formGroup = this.createOndernemingFormGroup(ondernemingenLijst[i]);
                formArray.push(formGroup);
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    disableFormGroup(formGroup: FormGroup | AbstractControl): void {
        if (formGroup instanceof FormGroup) {
            Object.keys(formGroup.controls).forEach((key) => {
                const control = formGroup.get(key);
                if (control instanceof FormControl) {
                    control.disable();
                } else if (control instanceof FormGroup) {
                    this.disableFormGroup(control);
                }
            });
        }
    }

    private getRelevantAangifte(): Aangifte | undefined {
        return this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte
            : this.incomeTaxEntity.aangifte;
    }

    private getOndernemingen(): Onderneming[] | undefined {
        const aangifte = this.getRelevantAangifte();
        return aangifte?.box1?.winstUitOnderneming?.ondernemingen;
    }

    public heeftOndernemingen(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen && ondernemingen.length > 0;
    }

    private berekenVermogensvergelijking(rapport: AangifteRapport) {
        if (rapport.specificatieJaarrekening) {
            // resetFormGroups() ← hier weghalen, staat al in updateRapportData
            const winstVolgensVergelijking = this.calculateWinstVolgensVergelijking(rapport);
            const verschilVermogen = this.calculateVerschilVermogen(rapport);
            this.specJaarrekeningFg.patchValue(rapport);
            this.tellingFG.patchValue({
                verschilVermogen: verschilVermogen,
                winstVolgensVergelijking: winstVolgensVergelijking
            });
        }
    }
}
